import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';

/**
 * FranceClosureMap — Carte 3D dynamique de la France
 * Affiche des points rouges (restaurants qui ferment) apparaissant aléatoirement
 * sur les grandes villes, avec animation pulse ring + compteur live.
 *
 * Pur SVG + framer-motion, aucune dépendance carto externe.
 */

// Grandes villes françaises avec coordonnées normalisées (viewBox 0-100)
// Calibrées approximativement sur le path France ci-dessous
const CITIES = [
  { name: 'Paris', x: 50, y: 26, weight: 6 },        // weight = fréquence d'apparition
  { name: 'Lyon', x: 60, y: 54, weight: 4 },
  { name: 'Marseille', x: 63, y: 75, weight: 4 },
  { name: 'Lille', x: 54, y: 10, weight: 3 },
  { name: 'Toulouse', x: 44, y: 73, weight: 3 },
  { name: 'Bordeaux', x: 33, y: 62, weight: 3 },
  { name: 'Nantes', x: 27, y: 44, weight: 3 },
  { name: 'Strasbourg', x: 83, y: 30, weight: 2 },
  { name: 'Nice', x: 78, y: 73, weight: 3 },
  { name: 'Montpellier', x: 53, y: 73, weight: 2 },
  { name: 'Rennes', x: 26, y: 34, weight: 2 },
  { name: 'Reims', x: 60, y: 24, weight: 1 },
  { name: 'Grenoble', x: 65, y: 58, weight: 1 },
  { name: 'Dijon', x: 62, y: 41, weight: 1 },
  { name: 'Le Havre', x: 41, y: 22, weight: 1 },
];

// Path SVG de la France métropolitaine — silhouette reconnaissable
// Basée sur une simplification natural-earth, viewBox 0-100
const FRANCE_PATH = `
  M 41,9
  L 44,8
  L 47,9
  L 49,11
  L 51,9
  L 54,8
  L 58,9
  L 62,11
  L 66,13
  L 69,15
  L 71,17
  L 73,19
  L 75,22
  L 78,24
  L 81,27
  L 83,30
  L 84,33
  L 83,36
  L 82,38
  L 80,40
  L 79,42
  L 80,44
  L 82,46
  L 84,49
  L 85,52
  L 84,55
  L 82,58
  L 79,61
  L 76,64
  L 75,66
  L 77,68
  L 78,71
  L 76,73
  L 73,74
  L 70,75
  L 67,75
  L 64,76
  L 61,76
  L 57,77
  L 53,78
  L 49,79
  L 45,79
  L 41,78
  L 38,76
  L 35,74
  L 32,71
  L 29,68
  L 27,65
  L 25,61
  L 23,57
  L 22,53
  L 21,49
  L 20,45
  L 19,42
  L 21,40
  L 19,38
  L 17,36
  L 15,33
  L 14,30
  L 16,27
  L 18,25
  L 21,23
  L 24,22
  L 26,20
  L 27,17
  L 29,15
  L 32,13
  L 35,12
  L 38,11
  L 41,9
  Z
`;

const FranceClosureMap = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const [activeClosures, setActiveClosures] = useState([]); // [{id, x, y, city}]
  const [counter, setCounter] = useState(0);

  // Lancement des fermetures aléatoires une fois visible
  useEffect(() => {
    if (!isInView) return;

    const weightedCities = CITIES.flatMap((c) => Array(c.weight).fill(c));
    let idCounter = 0;
    let counterValue = 0;

    // Tick initial (3 fermetures rapides au démarrage)
    const seedClosures = () => {
      for (let i = 0; i < 3; i++) {
        setTimeout(() => spawnClosure(), i * 350);
      }
    };

    const spawnClosure = () => {
      const city = weightedCities[Math.floor(Math.random() * weightedCities.length)];
      // Léger jitter autour de la ville pour ne pas empiler les points
      const jx = (Math.random() - 0.5) * 4;
      const jy = (Math.random() - 0.5) * 4;
      const id = ++idCounter;
      counterValue += 1;
      setCounter(counterValue);

      setActiveClosures((prev) => [...prev, { id, x: city.x + jx, y: city.y + jy, city: city.name }]);

      // Auto-remove after pulse animation
      setTimeout(() => {
        setActiveClosures((prev) => prev.filter((c) => c.id !== id));
      }, 2800);
    };

    seedClosures();
    // Nouvelle fermeture toutes les 900-1400ms
    const interval = setInterval(spawnClosure, 1100);
    return () => clearInterval(interval);
  }, [isInView]);

  return (
    <div ref={ref} className="relative w-full max-w-md mx-auto" data-testid="france-closure-map">
      {/* Carte 3D avec perspective */}
      <motion.div
        initial={{ opacity: 0, scale: 0.92, rotateX: 30 }}
        animate={isInView ? { opacity: 1, scale: 1, rotateX: 18 } : {}}
        transition={{ duration: 0.9, ease: 'easeOut' }}
        style={{
          transformStyle: 'preserve-3d',
          transformOrigin: 'center bottom',
        }}
        className="relative aspect-[5/6]"
      >
        <svg
          viewBox="0 0 100 100"
          preserveAspectRatio="xMidYMid meet"
          className="w-full h-full"
          style={{ filter: 'drop-shadow(0 25px 35px rgba(239, 68, 68, 0.25))' }}
        >
          {/* Background grid */}
          <defs>
            <pattern id="map-grid" width="6" height="6" patternUnits="userSpaceOnUse">
              <path d="M 6 0 L 0 0 0 6" fill="none" stroke="rgba(239,68,68,0.05)" strokeWidth="0.3" />
            </pattern>
            <linearGradient id="france-gradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#2a1a1a" />
              <stop offset="100%" stopColor="#1a0e0e" />
            </linearGradient>
            <radialGradient id="closure-glow" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#ef4444" stopOpacity="0.8" />
              <stop offset="100%" stopColor="#ef4444" stopOpacity="0" />
            </radialGradient>
          </defs>

          {/* Grid background */}
          <rect width="100" height="100" fill="url(#map-grid)" opacity="0.5" />

          {/* France silhouette - shadow layer (3D depth) */}
          <path
            d={FRANCE_PATH}
            fill="rgba(0,0,0,0.4)"
            transform="translate(1, 2)"
          />

          {/* France silhouette - main */}
          <path
            d={FRANCE_PATH}
            fill="url(#france-gradient)"
            stroke="#F05A1A"
            strokeWidth="0.6"
            strokeOpacity="0.5"
          />

          {/* City dots (static, neutral) */}
          {CITIES.map((c) => (
            <circle
              key={c.name}
              cx={c.x}
              cy={c.y}
              r="0.6"
              fill="#F05A1A"
              opacity="0.5"
            />
          ))}

          {/* Closures — animated red pulses */}
          <AnimatePresence>
            {activeClosures.map((closure) => (
              <g key={closure.id}>
                {/* Outer expanding ring */}
                <motion.circle
                  cx={closure.x}
                  cy={closure.y}
                  fill="none"
                  stroke="#ef4444"
                  strokeWidth="0.4"
                  initial={{ r: 0.5, opacity: 0.9 }}
                  animate={{ r: 6, opacity: 0 }}
                  transition={{ duration: 2.4, ease: 'easeOut' }}
                />
                {/* Inner expanding ring */}
                <motion.circle
                  cx={closure.x}
                  cy={closure.y}
                  fill="none"
                  stroke="#fca5a5"
                  strokeWidth="0.25"
                  initial={{ r: 0.3, opacity: 1 }}
                  animate={{ r: 3.5, opacity: 0 }}
                  transition={{ duration: 1.8, ease: 'easeOut', delay: 0.15 }}
                />
                {/* Glow halo */}
                <motion.circle
                  cx={closure.x}
                  cy={closure.y}
                  r="3"
                  fill="url(#closure-glow)"
                  initial={{ opacity: 0.6, scale: 0.5 }}
                  animate={{ opacity: 0, scale: 1.5 }}
                  transition={{ duration: 1.4, ease: 'easeOut' }}
                />
                {/* Core dot */}
                <motion.circle
                  cx={closure.x}
                  cy={closure.y}
                  fill="#ef4444"
                  initial={{ r: 0, opacity: 0 }}
                  animate={{ r: [0, 1.4, 1.1], opacity: [0, 1, 0.85] }}
                  exit={{ opacity: 0, r: 0 }}
                  transition={{ duration: 0.5 }}
                />
              </g>
            ))}
          </AnimatePresence>
        </svg>

        {/* Floating live counter */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.6, duration: 0.5 }}
          className="absolute top-2 right-2 bg-white/95 backdrop-blur-md rounded-xl px-3 py-2 shadow-[0_8px_24px_rgba(0,0,0,0.15)] border border-red-200"
          style={{ transform: 'translateZ(20px)' }}
          data-testid="closure-counter"
        >
          <div className="flex items-center gap-2">
            <span className="inline-flex w-2 h-2 rounded-full bg-red-500 animate-pulse shadow-[0_0_8px_rgba(239,68,68,0.8)]" />
            <p className="text-[9px] font-black uppercase tracking-widest text-red-500 leading-none">EN DIRECT</p>
          </div>
          <p className="font-bebas text-2xl text-red-500 leading-none mt-1 tabular-nums">
            {counter}
          </p>
          <p className="text-[9px] text-gray-500 leading-tight">restos fermés<br/>depuis ouverture page</p>
        </motion.div>

        {/* Bottom legend */}
        <div className="absolute bottom-1 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-white/90 backdrop-blur-md rounded-full px-3 py-1.5 shadow-md">
          <span className="inline-flex w-1.5 h-1.5 rounded-full bg-red-500" />
          <p className="text-[9px] font-bold text-gray-700 uppercase tracking-wider">Resto qui ferme</p>
        </div>
      </motion.div>

      {/* Caption below map */}
      <motion.p
        initial={{ opacity: 0 }}
        animate={isInView ? { opacity: 1 } : {}}
        transition={{ delay: 1.2, duration: 0.6 }}
        className="text-center text-[11px] text-[#1a1a1a]/55 italic mt-4 leading-relaxed"
      >
        Visualisation des fermetures · Source : INSEE, Banque de France 2024<br/>
        <span className="text-red-500 font-bold">42% des restaurants ferment dans les 3 premières années</span>
      </motion.p>
    </div>
  );
};

export default FranceClosureMap;
